import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { VimeoBackground } from './VimeoBackground';
const VIMEO_ORIGIN = 'https://player.vimeo.com';
interface LandingPageProps {
  onSkip: () => void;
  onLearnMore: () => void;
  isAuthenticated?: boolean;
}
export function LandingPage({
  onSkip,
  onLearnMore,
  isAuthenticated
}: LandingPageProps) {
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const sendVimeoCommand = useCallback((method: string, value?: unknown) => {
    const iframe = iframeRef.current || document.getElementById('vimeo-bg') as HTMLIFrameElement;
    if (!iframe?.contentWindow) return;
    const data: Record<string, unknown> = {
      method
    };
    if (value !== undefined) data.value = value;
    iframe.contentWindow.postMessage(JSON.stringify(data), VIMEO_ORIGIN);
  }, []);
  useEffect(() => {
    iframeRef.current = document.getElementById('vimeo-bg') as HTMLIFrameElement;
  }, []);
  const toggleMute = () => {
    if (isMuted) {
      sendVimeoCommand('setMuted', false);
      sendVimeoCommand('setVolume', 0.8);
      setIsMuted(false);
    } else {
      sendVimeoCommand('setMuted', true);
      setIsMuted(true);
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 1.5,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative w-full h-screen overflow-hidden bg-obsidian" data-id="element-34">
      <VimeoBackground data-id="element-35" />

      {/* Top Navigation */}
      <div className="absolute top-0 left-0 w-full p-6 md:p-10 flex justify-between items-start z-10" data-id="element-36">
        <div className="opacity-0 animate-fade-in" style={{
        animationDelay: '0.5s'
      }} data-id="element-37" />
        <button onClick={onSkip} className="text-offwhite/70 hover:text-offwhite uppercase tracking-[0.2em] text-xs font-medium transition-colors duration-500 opacity-0 animate-fade-in" style={{
        animationDelay: '1s'
      }} data-id="element-38">
          {isAuthenticated ? 'View Presentation →' : 'Skip →'}
        </button>
      </div>

      {/* Sound Toggle — bottom-left, always visible */}
      <button onClick={toggleMute} className="absolute bottom-12 md:bottom-20 left-6 md:left-10 z-20 flex items-center gap-2 text-offwhite/60 hover:text-offwhite transition-colors duration-300 cursor-pointer" aria-label={isMuted ? 'Unmute video' : 'Mute video'} type="button" data-id="element-39">
        <div className="w-10 h-10 flex items-center justify-center rounded-full border border-offwhite/25 backdrop-blur-sm hover:border-offwhite/50 transition-all duration-300 bg-obsidian/30" data-id="element-40">
          {isMuted ? <VolumeXIcon className="w-4 h-4" data-id="element-41" /> : <Volume2Icon className="w-4 h-4" data-id="element-42" />}
        </div>
      </button>

      {/* Bottom Action */}
      <div className="absolute bottom-12 md:bottom-20 left-0 w-full flex flex-col items-center gap-6 z-10" data-id="element-43">
        {isAuthenticated ? <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1.5,
        delay: 1.5
      }} onClick={onSkip} className="flex flex-col items-center gap-3 text-offwhite/60 hover:text-offwhite transition-colors duration-500" data-id="element-44">
            <span className="uppercase tracking-[0.15em] text-sm" data-id="element-45">
              Scroll to Presentation
            </span>
            <motion.div animate={{
          y: [0, 8, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-46">
              <ChevronDownIcon className="w-6 h-6" data-id="element-47" />
            </motion.div>
          </motion.button> : <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1.5,
        delay: 1.5
      }} onClick={onLearnMore} className="px-8 py-4 border border-offwhite/30 text-offwhite uppercase tracking-[0.15em] text-sm hover:bg-offwhite hover:text-obsidian transition-all duration-700 backdrop-blur-sm box-glow-hover" data-id="element-48">
            Learn More
          </motion.button>}
      </div>
    </motion.div>;
}